import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import OrderShowHeader, { OrderShowOrder, QueuedOrderSummary } from './OrderShowHeader'
import SectionAccordion from './shared/SectionAccordion'
import Timeline from './shared/Timeline'
import DisposalNotice from './shared/DisposalNotice'
import InforDataSection from './shared/sections/InforDataSection'
import SmsSection from './shared/sections/SmsSection'
import VouchersSection from './shared/sections/VouchersSection'
import { LocalizationContext, LocalizationContextType, DEFAULT_LOCALIZATION_CONTEXT } from '../shared/contexts'

export interface OrderShowProps {
  order: OrderShowOrder
  localization_context?: LocalizationContextType
  can_view_queued_orders?: boolean
  can_view_vouchers?: boolean
  can_create_voucher?: boolean
  can_invalidate_voucher?: boolean
}

const QueuedOrderCard = ({ queuedOrder, canViewDetails }: { queuedOrder: QueuedOrderSummary, canViewDetails: boolean }) => (
  <Box className='order-show-qo-card'>
    <div className='order-show-qo-card-header'>
      <Typography variant='h6'>
        {queuedOrder.recipe_name || 'Unknown recipe'}
        <span className='qo-id'> - Order Item #{queuedOrder.id}</span>
        <Chip className='ms-2' label={queuedOrder.state.replaceAll('_', ' ')} size='small' />
        <DisposalNotice
          markedForDisposal={queuedOrder.marked_for_disposal}
          disposalReason={queuedOrder.disposal_reason}
          disposedAt={queuedOrder.disposed_at}
          variant='badge'
        />
      </Typography>
      {canViewDetails &&
        <a className='btn btn-sm bw-button' href={`/queued_orders/${queuedOrder.id}`}>
          See details
        </a>
      }
    </div>
    <Timeline timeline={queuedOrder.timeline} title='' />
  </Box>
)

const OrderShow = ({
  order,
  localization_context,
  can_view_queued_orders,
  can_view_vouchers,
  can_create_voucher,
  can_invalidate_voucher,
}: OrderShowProps) => {
  const isInforOrder = order.order_provider?.provider_type === 'INFOR'
  const basePath = `/orders/${order.id}`

  return (
    <LocalizationContext.Provider value={localization_context || DEFAULT_LOCALIZATION_CONTEXT}>
      <Box className='order-show'>
        <OrderShowHeader order={order} />

        <Box className='order-show-queued-orders'>
          <Typography variant='h5' className='order-show-queued-orders-title'>
            Order items ({order.queued_orders.length})
          </Typography>
          {order.queued_orders.map((queuedOrder) => (
            <QueuedOrderCard key={queuedOrder.id} queuedOrder={queuedOrder} canViewDetails={!!can_view_queued_orders} />
          ))}
        </Box>

        <SectionAccordion title='SMS notifications'>
          <SmsSection basePath={basePath} />
        </SectionAccordion>

        {can_view_vouchers && (
          <SectionAccordion title='Vouchers'>
            <VouchersSection
              basePath={basePath}
              orderId={order.id}
              canCreate={!!can_create_voucher}
              canInvalidate={!!can_invalidate_voucher}
            />
          </SectionAccordion>
        )}

        {isInforOrder &&
          <SectionAccordion title='Infor data'>
            <InforDataSection
              basePath={basePath}
              alreadyCached={order.infor_metadata_present}
              fetchedAt={order.infor_metadata_fetched_at}
            />
          </SectionAccordion>
        }
      </Box>
    </LocalizationContext.Provider>
  )
}

export default OrderShow
